import * as React from 'react';
import {useSize} from '@packages/utilities/styled';
import {CheckDefaultIcon} from '../../icons';
import type {
  SizeContextSize,
  HTMLAttributes,
  SpringTooling,
} from '../__SharedTypes';
import {RadioButtonView} from './Radio.styles';

export type RadioButtonProps = {
  /**
   * Renders the style of the checked indicator.
   */
  checkedIndicator?: 'checkbox' | 'radio';
  /**
   * The color of the radio in its non-error state.
   */
  colorVariant?: 'blue' | 'purple' | 'green';
  /**
   * Renders the checked state for the RadioButton.
   */
  isChecked?: boolean;
  /**
   * Renders the disabled state for the RadioButton.
   */
  isDisabled?: boolean;
  /**
   * Renders the error state for the RadioButton.
   */
  isError?: boolean;
  /**
   * Renders the focus ring for the RadioButton.
   */
  isFocused?: boolean;
  /**
   * The size of the RadioButton.
   */
  size?: SizeContextSize;
} & HTMLAttributes &
  SpringTooling;

// @ts-expect-error - TS7031 - Binding element 'isChecked' implicitly has an 'any' type. | TS7031 - Binding element 'checkedIndicator' implicitly has an 'any' type. | TS7031 - Binding element 'size' implicitly has an 'any' type.
const RadioCheckIndicator = ({isChecked, checkedIndicator, size}) => {
  if (!isChecked) return null;
  if (checkedIndicator !== 'checkbox') return null;

  const isMedium = size === 'medium';
  const iconSize = isMedium ? 8 : 12;

  return <CheckDefaultIcon size={iconSize} />;
};

/**
 * An indicator for components like `Radio` and `RadioCard`.
 *
 * @component
 */
export const RadioButton = React.forwardRef<HTMLDivElement, RadioButtonProps>(
  (
    {
      colorVariant = 'blue',
      isChecked = false,
      isDisabled = false,
      isError = false,
      size: sizeProp,
      ...props
    }: RadioButtonProps,
    forwardedRef
  ) => {
    const size = useSize(sizeProp);

    return (
      <RadioButtonView
        {...props}
        colorVariant={colorVariant}
        isChecked={isChecked}
        isDisabled={isDisabled}
        isError={isError}
        ref={forwardedRef}
      >
        <RadioCheckIndicator
          checkedIndicator="radio"
          isChecked={isChecked}
          size={size}
        />
      </RadioButtonView>
    );
  }
);

RadioButton.displayName = 'RadioButton';
